import { AxiosResponse } from "axios";
import ApiResponse from "../Models/ApiResponse";
import { Page } from "../Models/Page";
import ReferenceModel from "../Models/ReferenceModel";
import api from "../Interceptors/Interceptor";
import { BaseService } from "./BaseService";
import { GenericSelectItemService } from "./GenericSelectItemService";

export interface IWorkflowSpotlightItem {
    uuid: string;
    title: string;
    progressPct: number;
    type?: string;
}

export interface IWorkflowSpotlight {
    best: IWorkflowSpotlightItem[];
    worst: IWorkflowSpotlightItem[];
    hasContent: boolean;
}

export interface IProcessflowModel {
    uuid: string;
    customer?: ReferenceModel;
    title: string;
    description?: string;
    domain?: string;
    status?: string;
    severity?: string;
    type?: string;
    referenceDate?: string;
    tolerancePct?: number;
    dashboardId?: string;
    linkHash?: string;
    requiredPermission?: string;
    roles?: ReferenceModel[];
    scopes?: IProcessflowScope[];
    readinessPct?: number;
    totalSteps?: number;
    blockingSteps?: number;
    automaticSteps?: number;
    manualSteps?: number;
    completedSteps?: number;
    relatedGroupSize?: number;
    active?: boolean;
    createdAt?: string;
    updatedAt?: string;
    lastActivityAt?: string;
}

export interface IProcessflowScope {
    uuid?: string;
    processflowId?: string;
    metricViewUuid?: string;
    dataType?: string;
    operator?: string;
    fieldName: string;
    fieldValue: string;
}

export interface IProcessflowAutomaticCriteriaCondition {
    parameter?: string;
    sqlOperator?: string;
    values?: string[];
    value?: string;
    dataType?: string;
    valueKind?: string;
}

export interface IProcessflowAutomaticCriteria {
    metricViewUuid?: string;
    datapoint?: string;
    functionName?: string;
    datapointExpression?: string;
    datapointBuilderStateJson?: string;
    datapointConfigurationMode?: string;
    operatorCode?: string;
    targetValue?: number;
    targetUpperValue?: number;
    tolerancePct?: number;
    evaluationPeriodCode?: string;
    evaluationPeriodValue?: { dateFrom?: string; dateTill?: string; dateYear?: number };
    evaluationFrequencyCode?: string;
    conditions?: IProcessflowAutomaticCriteriaCondition[];
}

/** Canonical PROCESSFLOW_STEP_STATUS_OPTION codes. */
export type ProcessflowStepStatus =
    | "initial"
    | "in_progress"
    | "cancelled"
    | "completed_on_time"
    | "completed_late";

export interface IProcessflowStep {
    uuid: string;
    processflowId?: string;
    stepName: string;
    description?: string;
    owner?: string;
    completionRuleKey?: string;
    stepType: "automatic" | "manual";
    isBlocking: boolean;
    timingValue: number;
    timingUnit: string;
    timingDirection: string;
    completionDelayValue?: number;
    completionDelayUnit?: string;
    stepWeight?: number;
    orderIndex: number;
    status?: ProcessflowStepStatus;
    completedAt?: string;
    reporterUser?: ReferenceModel;
    assignedUser?: ReferenceModel;
    completionOperatorCode?: string;
    completionTargetValue?: number;
    completionTargetUpperValue?: number;
    completionTolerancePct?: number;
    evaluationCriteria?: {
        uuid?: string;
        metricView?: ReferenceModel;
        metricDatasetName?: string;
        metricTableName?: string;
        currentValue?: number;
        nextRunTs?: string;
        lastRunTs?: string;
        latestResultExecutedAt?: string;
        latestResultStatus?: string;
        latestResultContext?: string;
        datapoint?: string;
        functionName?: string;
        evaluationPeriod?: ReferenceModel;
        evaluationFrequency?: ReferenceModel;
        conditions?: Array<{
            parameter?: string;
            sqlOperator?: string;
            values?: string[];
            value?: string;
            dataType?: string;
        }>;
    };
}

export interface IAssignedProcessflowStep extends IProcessflowStep {
    customer?: ReferenceModel;
    processflowTitle?: string;
    processflowStatus?: string;
    processflowSeverity?: string;
    processflowDomain?: string;
}

export interface IProcessflowTask {
    uuid: string;
    processflowId: string;
    stepUuid?: string;
    title: string;
    description?: string;
    status: "open" | "in_progress" | "waiting" | "done" | "cancelled";
    priority?: "low" | "medium" | "high";
    createdAt?: string;
    createdBy?: ReferenceModel;
    assignedUser?: ReferenceModel;
    deadline?: string;
}

export interface IAssignedProcessflowTask extends IProcessflowTask {
    customer?: ReferenceModel;
    stepName?: string;
    processflowTitle?: string;
    processflowStatus?: string;
    processflowSeverity?: string;
    processflowDomain?: string;
}

export interface IProcessflowActivity {
    uuid: string;
    processflowId: string;
    eventType: "created" | "updated" | "task_added" | "task_updated" | "task_assignee_change" | "comment" | "message" | "status_changed" | "email_sent" | "assignee_change" | "step_note";
    contentTypeIcon?: string;
    contentTypeDescription?: string;
    payload?: Record<string, any>;
    actorName?: string;
    createdBy?: string;
    createdAt: string;
}

export interface IProcessflowReadinessStepResult {
    stepUuid?: string;
    status: ProcessflowStepStatus;
    deadline?: string;
}

export interface IProcessflowReadinessRow {
    objectName: string;
    ocwDate?: string;
    readinessPct: number;
    stepResults: IProcessflowReadinessStepResult[];
}

export interface IProcessflowPayload {
    customer?: ReferenceModel;
    title: string;
    description?: string;
    domain?: string;
    severity?: string;
    type?: string;
    referenceDate?: string;
    tolerancePct?: number;
    dashboardId?: string;
    linkHash?: string;
    roles?: ReferenceModel[];
    scopes?: IProcessflowScope[];
    steps?: Array<{
        stepName: string;
        description?: string;
        owner?: string;
        completionRuleKey?: string;
        automaticCriteria?: IProcessflowAutomaticCriteria;
        stepType: "automatic" | "manual";
        isBlocking: boolean;
        timingValue: number;
        timingUnit: string;
        timingDirection: string;
        completionDelayValue?: number;
        completionDelayUnit?: string;
        reporterUserUuid?: string;
        stepWeight?: number;
        orderIndex: number;
    }>;
}

export interface IProcessflowStepCriteriaPreviewPayload {
    metricUuid?: string;
    metricViewUuid: string;
    datapoint: string;
    functionName?: string;
    datapointExpression?: string;
    datapointBuilderStateJson?: string;
    datapointConfigurationMode?: string;
    evaluationPeriod?: string;
    scopes?: Array<{
        metricViewUuid?: string;
        dataType?: string;
        operator?: string;
        fieldName: string;
        fieldValue: string;
    }>;
    conditions?: Array<{
        parameter: string;
        sqlOperator: string;
        values?: string[];
        value?: string;
        dataType?: string;
    }>;
}

export interface IProcessflowStepCriteriaPreviewResult {
    value?: number;
    sampleSize?: number;
    evaluationPeriod?: string;
}

export interface IProcessflowStepCriteriaExecutionQueryResult {
    query?: string;
    previousPeriodQuery?: string;
}

export interface IEvaluationCriteriaResultHistoryItem {
    uuid: string;
    evaluationCriteriaUuid?: string;
    executedAt?: string;
    value?: number;
    currentValue?: number;
    sampleSize?: number;
    resultStatus?: string;
    resultContext?: string;
}

export interface IHomeProcessflow {
    behindCount: number;
    flowUuids: string[];
}

export class ProcessflowService extends BaseService<IProcessflowModel, IProcessflowModel | IProcessflowPayload> {
    private genericSelectItemService = new GenericSelectItemService();

    public async fetchItems(params?: any): Promise<AxiosResponse<ApiResponse<Page<IProcessflowModel>>>> {
        return await api.get(`v1/${this.modelUrl()}`, { params });
    }

    public async fetchSpotlight(params?: {
        type?: string;
        customer?: string;
    }): Promise<AxiosResponse<ApiResponse<IWorkflowSpotlight>>> {
        return await api.get(`v1/${this.modelUrl()}/spotlight`, { params });
    }

    public async fetchHome(params?: {
        type?: string;
        customer?: string;
    }): Promise<AxiosResponse<ApiResponse<IHomeProcessflow>>> {
        return await api.get(`v1/${this.modelUrl()}/home`, { params });
    }

    public async fetchItem(uuid: string): Promise<AxiosResponse<ApiResponse<IProcessflowModel>>> {
        return await api.get(`v1/${this.modelUrl()}/${uuid}`);
    }

    public async fetchSteps(processflowId: string): Promise<AxiosResponse<ApiResponse<IProcessflowStep[]>>> {
        return await api.get(`v1/${this.modelUrl()}/${processflowId}/step`);
    }

    public async fetchAssignedToCurrentUserSteps(params?: {
        search?: string;
        status?: string;
        severity?: string;
        domain?: string;
        customer?: string;
    }): Promise<AxiosResponse<ApiResponse<IAssignedProcessflowStep[]>>> {
        return await api.get(`v1/${this.modelUrl()}/step/assigned-to-me`, { params });
    }

    public async fetchAssignedToCurrentUserTasks(params?: {
        search?: string;
        status?: string;
        severity?: string;
        domain?: string;
        customer?: string;
    }): Promise<AxiosResponse<ApiResponse<IAssignedProcessflowTask[]>>> {
        return await api.get(`v1/${this.modelUrl()}/task/assigned-to-me`, { params });
    }

    public async fetchTasks(processflowId: string): Promise<AxiosResponse<ApiResponse<IProcessflowTask[]>>> {
        return await api.get(`v1/${this.modelUrl()}/${processflowId}/task`);
    }

    public async addTask(
        processflowId: string,
        payload: { stepUuid?: string; title: string; description?: string; priority?: "low" | "medium" | "high"; assignedUserUuid?: string | null }
    ): Promise<AxiosResponse<ApiResponse<IProcessflowTask>>> {
        return await api.post(`v1/${this.modelUrl()}/${processflowId}/task`, payload);
    }

    public async addStepTask(
        stepUuid: string,
        payload: { title: string; description?: string; priority?: "low" | "medium" | "high"; assignedUserUuid?: string | null }
    ): Promise<AxiosResponse<ApiResponse<IProcessflowTask>>> {
        return await api.post(`v1/${this.modelUrl()}/step/${stepUuid}/task`, payload);
    }

    public async updateTask(
        taskUuid: string,
        payload: Partial<IProcessflowTask>
    ): Promise<AxiosResponse<ApiResponse<IProcessflowTask>>> {
        return await api.put(`v1/${this.modelUrl()}/task/${taskUuid}`, payload);
    }

    public async assignTaskUser(taskUuid: string, assignedUserUuid: string | null): Promise<AxiosResponse<ApiResponse<IProcessflowTask>>> {
        return await api.put(`v1/${this.modelUrl()}/task/${taskUuid}/assignee`, { assignedUserUuid });
    }

    public async fetchActivity(processflowId: string): Promise<AxiosResponse<ApiResponse<IProcessflowActivity[]>>> {
        return await api.get(`v1/${this.modelUrl()}/${processflowId}/activity`);
    }

    public async addComment(processflowId: string, text: string): Promise<AxiosResponse<ApiResponse<IProcessflowActivity>>> {
        return await api.post(`v1/${this.modelUrl()}/${processflowId}/comment`, { text });
    }

    public async deleteComment(processflowId: string, commentUuid: string): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return await api.delete(`v1/${this.modelUrl()}/${processflowId}/comment/${commentUuid}`);
    }

    public async fetchStepActivity(stepUuid: string): Promise<AxiosResponse<ApiResponse<IProcessflowActivity[]>>> {
        return await api.get(`v1/${this.modelUrl()}/step/${stepUuid}/activity`);
    }

    public async updateStepStatus(
        stepUuid: string,
        status: IProcessflowStep["status"]
    ): Promise<AxiosResponse<ApiResponse<IProcessflowStep>>> {
        return await api.put(`v1/${this.modelUrl()}/step/${stepUuid}/status`, { status });
    }

    public async assignStepUser(stepUuid: string, assignedUserUuid: string | null): Promise<AxiosResponse<ApiResponse<IProcessflowStep>>> {
        return await api.put(`v1/${this.modelUrl()}/step/${stepUuid}/assignee`, { assignedUserUuid });
    }

    public async addEmailEvent(processflowId: string, to: string, subject: string, body?: string): Promise<AxiosResponse<ApiResponse<IProcessflowActivity>>> {
        return await api.post(`v1/${this.modelUrl()}/${processflowId}/email-event`, { to, subject, body });
    }

    public async fetchReadinessMatrix(processflowId: string): Promise<AxiosResponse<ApiResponse<IProcessflowReadinessRow[]>>> {
        return await api.get(`v1/${this.modelUrl()}/${processflowId}/readiness`);
    }

    public async previewStepCriteria(
        payload: IProcessflowStepCriteriaPreviewPayload
    ): Promise<AxiosResponse<ApiResponse<IProcessflowStepCriteriaPreviewResult>>> {
        return await api.post(`v1/${this.modelUrl()}/step/criteria-preview`, payload);
    }

    public async fetchStepCriteriaExecutionQuery(
        payload: IProcessflowStepCriteriaPreviewPayload
    ): Promise<AxiosResponse<ApiResponse<IProcessflowStepCriteriaExecutionQueryResult>>> {
        return await api.post(`v1/${this.modelUrl()}/step/criteria-execution-query`, payload);
    }

    public async fetchStepCriteriaHistory(
        stepUuid: string,
        limit: number = 30
    ): Promise<AxiosResponse<ApiResponse<IEvaluationCriteriaResultHistoryItem[]>>> {
        return await api.get(`v1/${this.modelUrl()}/step/${stepUuid}/criteria-history`, { params: { limit } });
    }

    public async triggerStepCriteriaRefresh(stepUuid: string): Promise<AxiosResponse<ApiResponse<any>>> {
        return await api.post(`v1/${this.modelUrl()}/step/${stepUuid}/criteria-refresh`);
    }

    public async submitStepCriteriaManualInput(
        stepUuid: string,
        payload: { value: number; note?: string }
    ): Promise<AxiosResponse<ApiResponse<any>>> {
        return await api.post(`v1/${this.modelUrl()}/step/${stepUuid}/criteria-manual-input`, payload);
    }

    public async fetchSelectOptionsByType(type: string): Promise<AxiosResponse<ApiResponse<ReferenceModel[]>>> {
        return this.genericSelectItemService.fetchByType(type);
    }

    public async fetchDistinctDomains(): Promise<AxiosResponse<ApiResponse<string[]>>> {
        return await api.get(`v1/${this.modelUrl()}/domains`);
    }

    protected modelUrl(): string {
        return "processflow";
    }
}
